// The customers list's peek: who they are to us right now (the drones they hold, what is on
// order, who to call) without leaving the list. Contacts come from the list row; drones and
// orders from the customer page's own orders call, so the peek and the page never disagree.
// No money: the customer page shows none. docs/features/peek.md.

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { buttonClass } from '../components/ui/Button';
import Alert from '../components/ui/Alert';
import FadeIn from '../components/ui/FadeIn';
import EmptyState from '../components/ui/EmptyState';
import { ListRow, ListStack } from '../components/ui/ListRow';
import { FOCUS } from '../components/ui/motion';
import Peek, { PeekFact, PeekSection } from '../components/ui/Peek';
import type { PeekStep } from '../components/ui/Peek';
import Progress from '../components/ui/Progress';
import Skeleton from '../components/ui/Skeleton';
import { CATEGORY } from '../components/ui/categoryColors';
import Tag from '../components/ui/Tag';
import { ITEM_TYPES } from '../Sales/itemTypes';
import StatusPill from '../Sales/StatusPill';
import { readingText } from '../Sales/shipping';
import { formatTimestamp } from '../utils/date-utils';
import { getCustomerOrders } from './customersApi';
import { ORDER_TYPE_LABELS } from './types';
import type { Customer, CustomerOrder, CustomerOrders, CustomerTotal } from './types';

// Relationship colours are data, drawn through Tag with the word in it.
const RELATIONSHIP: { key: 'is_lessee' | 'is_renter' | 'is_purchaser'; word: string; hex: string }[] = [
  { key: 'is_lessee', word: 'Lease', hex: CATEGORY.blue.ink.hex },
  { key: 'is_renter', word: 'Rental', hex: CATEGORY.orange.ink.hex },
  { key: 'is_purchaser', word: 'Purchase', hex: CATEGORY.green.ink.hex },
];

// Mirrors the orders page's per-tab `closed` lists (Sales/OrdersPage.tsx TABS); change both.
const CLOSED: Record<string, string[]> = {
  sale: ['shipped', 'cancelled'],
  lease: ['returned', 'cancelled'],
  rental: ['returned', 'cancelled'],
};
export const isOpenOrder = (o: Pick<CustomerOrder, 'order_type' | 'status'>): boolean =>
  !(CLOSED[o.order_type] || CLOSED.sale).includes(o.status);

const DRONE_KEYS = new Set(ITEM_TYPES.filter((t) => t.stat === 'drones').map((t) => t.value));
const SHOWN_ORDERS = 5;

const LINK = `rounded-inner text-on-surface hover:text-primary transition-colors duration-quick ease-emphasized ${FOCUS}`;

const sum = (totals: CustomerTotal[], pick: (t: CustomerTotal) => number) =>
  totals.filter((t) => DRONE_KEYS.has(t.key)).reduce((n, t) => n + pick(t), 0);

/** One relationship's drones: how many, and a bar of how many have left the building. */
const Holding: React.FC<{ label: string; have: number; moved: number; word: string }> = ({ label, have, moved, word }) => (
  <div className="flex min-h-[1.75rem] items-center gap-3">
    <span className="w-20 flex-none text-[13px] text-on-surface-variant">{label}</span>
    <span className="w-10 flex-none text-right text-sm font-semibold tabular-nums text-on-surface">{have}</span>
    <Progress value={moved} max={have} label={`${label}: drones ${word}`} className="flex-1" />
    <span className="flex-none whitespace-nowrap text-xs tabular-nums text-on-surface-variant">{moved} {word}</span>
  </div>
);

const SectionError: React.FC<{ onRetry: () => void }> = ({ onRetry }) => (
  <Alert onRetry={onRetry}>Could not load this customer's drones and orders. Check your connection and try again.</Alert>
);

const Loading: React.FC<{ label: string }> = ({ label }) => (
  <div aria-busy="true" aria-label={label} className="space-y-2 py-1">
    <Skeleton className="h-4 w-full" />
    <Skeleton className="h-4 w-2/3" />
  </div>
);

const Drones: React.FC<{ data: CustomerOrders }> = ({ data }) => {
  const rows = [
    { label: 'Bought', have: sum(data.totals, (t) => t.purchased), moved: sum(data.totals, (t) => t.purchased_shipped), word: 'shipped' },
    { label: 'On lease', have: sum(data.totals, (t) => t.leased), moved: sum(data.totals, (t) => t.leased_out), word: 'out' },
    { label: 'On rental', have: sum(data.totals, (t) => t.rented), moved: sum(data.totals, (t) => t.rented_out), word: 'out' },
  ].filter((r) => r.have > 0);
  if (rows.length === 0) return <EmptyState variant="line">No drones bought, leased or rented yet.</EmptyState>;
  return <div className="space-y-1">{rows.map((r) => <Holding key={r.label} {...r} />)}</div>;
};

const OpenOrders: React.FC<{ orders: CustomerOrder[] }> = ({ orders }) => {
  const open = orders.filter(isOpenOrder);
  if (open.length === 0) return <EmptyState variant="line">No open orders.</EmptyState>;
  const more = open.length - SHOWN_ORDERS;
  return (
    <>
      <ListStack
        on="panel"
        label="Open orders"
        items={open.slice(0, SHOWN_ORDERS)}
        getKey={(o) => String(o.id)}
        renderRow={(o) => (
          <ListRow>
            <div className="flex items-center justify-between gap-2">
              <Link to={`/orders/${o.id}`} className={`min-w-0 truncate font-mono text-sm ${LINK}`}>{o.so_number}</Link>
              <span className="flex-none"><StatusPill status={o.status} /></span>
            </div>
            <div className="mt-0.5 flex items-center justify-between gap-2 text-xs text-on-surface-variant">
              <span>{ORDER_TYPE_LABELS[o.order_type] || o.order_type}</span>
              <span className="min-w-0 truncate tabular-nums">{readingText(o.reading) || `Created ${formatTimestamp(o.created_at, 'MMM D, YYYY')}`}</span>
            </div>
          </ListRow>
        )}
      />
      {more > 0 && <p className="m-0 mt-2 text-xs text-on-surface-variant">{more} more on the customer page.</p>}
    </>
  );
};

const Contact: React.FC<{ customer: Customer }> = ({ customer: c }) => {
  const phone = c.phone || c.contact_phone;
  if (!c.contact_name && !phone && !c.contact_email && !c.shipping_address) {
    return <EmptyState variant="line">No contact on file yet. Add one on the customer page.</EmptyState>;
  }
  return (
    <>
      <dl className="m-0 space-y-1">
        {c.contact_name && <PeekFact label="Name">{c.contact_name}</PeekFact>}
        {phone && <PeekFact label="Phone"><a href={`tel:${phone}`} className={LINK}>{phone}</a></PeekFact>}
        {c.contact_email && (
          <PeekFact label="Email" title={c.contact_email}><a href={`mailto:${c.contact_email}`} className={LINK}>{c.contact_email}</a></PeekFact>
        )}
      </dl>
      {c.shipping_address && (
        <div className="mt-2">
          <div className="text-[13px] text-on-surface-variant">Ships to</div>
          <p className="m-0 mt-0.5 whitespace-pre-line text-sm text-on-surface">{c.shipping_address}</p>
        </div>
      )}
    </>
  );
};

export interface CustomerPeekProps {
  /** The list row being shown; null closes the peek. */
  customer: Customer | null;
  onClose: () => void;
  returnTo: React.RefObject<HTMLElement | null>;
  step?: PeekStep;
}

const CustomerPeek: React.FC<CustomerPeekProps> = ({ customer, onClose, returnTo, step }) => {
  const id = customer?.id ?? null;
  // Keyed by id, so a step never shows the last customer's numbers under the next one's name.
  const [orders, setOrders] = useState<{ id: number; data: CustomerOrders } | null>(null);
  const [failed, setFailed] = useState(false);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    if (id == null) return undefined;
    let live = true;
    setFailed(false);
    getCustomerOrders(id)
      .then((data) => { if (live) setOrders({ id, data }); })
      .catch(() => { if (live) setFailed(true); });
    return () => { live = false; };
  }, [id, reload]);

  const data = orders && orders.id === id ? orders.data : null;
  const relationships = customer ? RELATIONSHIP.filter((r) => customer[r.key]) : [];

  return (
    <Peek
      open={!!customer}
      onClose={onClose}
      label={`Customer preview: ${customer?.name || ''}`}
      title={customer?.name}
      meta={customer ? (
        <>
          {customer.customer_code && <span className="font-mono text-xs text-on-surface-variant">{customer.customer_code}</span>}
          {relationships.map((r) => <Tag key={r.key} hex={r.hex}>{r.word}</Tag>)}
        </>
      ) : null}
      returnTo={returnTo}
      step={step}
      contentKey={id}
      footer={id != null ? <Link to={`/customers/${id}`} className={`${buttonClass('filled', 'md')} w-full`}>Open customer</Link> : null}
    >
      {customer ? (
        <div className="space-y-6">
          {failed ? (
            <SectionError onRetry={() => setReload((n) => n + 1)} />
          ) : (
            <>
              <PeekSection title="Drones">{data ? <FadeIn><Drones data={data} /></FadeIn> : <Loading label="Loading drones" />}</PeekSection>
              <PeekSection title="Open orders">{data ? <FadeIn><OpenOrders orders={data.orders} /></FadeIn> : <Loading label="Loading orders" />}</PeekSection>
            </>
          )}
          <PeekSection title="Contact"><Contact customer={customer} /></PeekSection>
          {customer.notes && (
            <PeekSection title="Notes">
              <p className="m-0 line-clamp-4 whitespace-pre-line text-sm text-on-surface">{customer.notes}</p>
            </PeekSection>
          )}
        </div>
      ) : null}
    </Peek>
  );
};

export default CustomerPeek;
